// Service pour gérer les opérations IndexedDB

const DB_NAME = 'prevision-heures-db';
const DB_VERSION = 5; // Incrémenter la version pour ajouter le nouveau store
const STORES = {
  EVENTS: 'events',
  PERIODS: 'periods',
  CUSTOM_COLORS: 'customColors',
  PREFS: 'prefs',
  SALAIRES: 'salaires',
  NOTES: 'notes' // <-- AJOUTER ICI
};

// Constantes pour les clés d'identification des périodes dans les événements
export const PERIOD_ID_KEYS = {
  START: 'periodStartId',
  END: 'periodEndId'
};

// Initialiser la base de données
export async function initDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('Erreur lors de l\'ouverture de la base de données', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      const oldVersion = event.oldVersion;

      console.log(`Mise à jour de la DB de la version ${oldVersion} vers ${DB_VERSION}`);

      // Créer les stores nécessaires pour la version 1
      if (oldVersion < 1) {
        if (!db.objectStoreNames.contains('events')) {
          const eventsStore = db.createObjectStore('events', { keyPath: 'id', autoIncrement: true });
          eventsStore.createIndex('startDate', 'startDate', { unique: false });
        }

        if (!db.objectStoreNames.contains('periods')) {
          db.createObjectStore('periods', { keyPath: 'id', autoIncrement: true });
        }
      }

      // Ajouter le store pour les couleurs personnalisées en version 2
      if (oldVersion < 2) {
        if (!db.objectStoreNames.contains('customColors')) {
          console.log('Création du store customColors');
          db.createObjectStore('customColors', { keyPath: 'id' });
        }
      }

      // Ajouter le store pour les préférences en version 3
      if (oldVersion < 3) {
        if (!db.objectStoreNames.contains('prefs')) {
          console.log('Création du store prefs');
          db.createObjectStore('prefs', { keyPath: 'id' });
        }
      }
      if (oldVersion < 4) {
        // Ajouter le store pour les salaires si absent
        if (!db.objectStoreNames.contains(STORES.SALAIRES)) {
          db.createObjectStore('salaires', { keyPath: 'eventId' });
        }
      }
      if (oldVersion < 5) {
        // Ajouter le store pour les notes
        if (!db.objectStoreNames.contains(STORES.NOTES)) {
          console.log('Création du store notes');
          db.createObjectStore('notes', { keyPath: 'id' });
        }
      }
    };
  });
}

// Fonction générique pour obtenir tous les éléments d'un store
export function getAllItems(storeName) {
  return new Promise((resolve, reject) => {
    initDB().then(db => {
      const transaction = db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      request.onsuccess = () => {
        resolve(request.result);
      };

      request.onerror = (event) => {
        console.error(`Erreur lors de la récupération des données de ${storeName}`, event);
        reject(`Erreur lors de la récupération des données de ${storeName}`);
      };
    }).catch(reject);
  });
}

// Fonction générique pour ajouter ou mettre à jour un élément
export function saveItem(storeName, item) {
  return new Promise((resolve, reject) => {
    initDB().then(db => {
      try {
        const transaction = db.transaction(storeName, 'readwrite');
        const store = transaction.objectStore(storeName);

        // S'assurer que l'élément est un objet valide
        if (!item || typeof item !== 'object') {
          reject(new Error('Invalid item: must be an object'));
          return;
        }

        // Vérifier que l'ID est présent et valide
        if (!item.id) {
          item.id = Date.now();
        }

        const request = store.put(item);

        request.onsuccess = () => {
          resolve(item);
        };

        request.onerror = (event) => {
          console.error(`Erreur lors de la sauvegarde dans ${storeName}`, event);
          reject(new Error(`Erreur lors de la sauvegarde dans ${storeName}: ${event.target.error}`));
        };
      } catch (error) {
        console.error(`Exception lors de la sauvegarde dans ${storeName}`, error);
        reject(error);
      }
    }).catch(error => {
      console.error(`Erreur lors de l'initialisation de la DB pour ${storeName}`, error);
      reject(error);
    });
  });
}

// Fonction générique pour sauvegarder plusieurs éléments
export function saveItems(storeName, items) {
  return new Promise((resolve, reject) => {
    initDB().then(db => {
      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);

      let count = 0;

      transaction.oncomplete = () => {
        resolve(items);
      };

      transaction.onerror = (event) => {
        console.error(`Erreur lors de la sauvegarde multiple dans ${storeName}`, event);
        reject(`Erreur lors de la sauvegarde multiple dans ${storeName}`);
      };

      items.forEach(item => {
        const request = store.put(item);
        request.onsuccess = () => {
          count++;
          if (count === items.length) {
            // Toutes les opérations sont terminées
          }
        };
      });
    }).catch(reject);
  });
}

// Fonction générique pour supprimer un élément
export function deleteItem(storeName, id) {
  return new Promise((resolve, reject) => {
    initDB().then(db => {
      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);

      // Assurer que l'ID est du bon type
      let key = id;
      if (typeof id === 'string' && !isNaN(id)) {
        key = parseInt(id);
      }

      const request = store.delete(key);

      request.onsuccess = () => {
        resolve(true);
      };

      request.onerror = (event) => {
        console.error(`Erreur lors de la suppression dans ${storeName}`, event);
        reject(`Erreur lors de la suppression dans ${storeName}`);
      };
    }).catch(reject);
  });
}

// Fonction de debug pour voir le contenu d'un store
export function debugStore(storeName) {
  return new Promise((resolve, reject) => {
    getAllItems(storeName).then(items => {
      console.log(`Contenu du store ${storeName}:`, items);
      resolve(items);
    }).catch(reject);
  });
}

// Exporter toutes les données de la base
export function exportDatabase() {
  return Promise.all([
    getAllItems(STORES.EVENTS),
    getAllItems(STORES.PERIODS),
    getAllItems(STORES.CUSTOM_COLORS) // Ajouter les couleurs personnalisées
  ]).then(([events, periods, customColors]) => {
    return {
      events,
      periods,
      customColors,
      exportDate: new Date().toISOString()
    };
  });
}

// Importer des données dans la base
export function importDatabase(data) {
  const promises = [];

  if (data.events) {
    promises.push(saveItems(STORES.EVENTS, data.events));
  }

  if (data.periods) {
    promises.push(saveItems(STORES.PERIODS, data.periods));
  }

  if (data.customColors) {
    promises.push(saveItems(STORES.CUSTOM_COLORS, data.customColors));
  }

  return Promise.all(promises).then(() => {
    return true;
  });
}

// Obtenir les noms de stores pour l'export/import
export function getStoreNames() {
  return STORES;
}

// Ajouter les fonctions manquantes pour l'export/import
export function getAllFromStore(db, storeName) {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export function clearStore(db, storeName) {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();

    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export function importToStore(db, storeName, data) {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);

    let count = 0;
    const total = data.length;

    if (total === 0) {
      resolve();
      return;
    }

    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);

    data.forEach(item => {
      const request = store.add(item);
      request.onsuccess = () => {
        count++;
        if (count === total) {
          // Toutes les données ont été ajoutées
        }
      };
      request.onerror = () => reject(request.error);
    });
  });
}
